import { createContext } from '@chakra-ui/react-utils'
import { useMemo, useEffect, cloneElement } from 'react'
import { useRoutes, useParams, useNavigate } from 'react-router-dom'

const [WizardProvider, useWizardContext] = createContext({
  name: 'WizardContext',
  errorMessage:
    'useWizardContext: `context` is undefined. Seems you forgot to wrap the components in `<Wizard />`'
})

export const useWizard = ({ data, steps = [], onStep, onFinish, defaultValues }) => {
  const params = useParams()
  const navigate = useNavigate()

  const currentStepIndex = steps.findIndex(step => step.path === params['*'])
  const currentStep = steps[currentStepIndex]

  const redirect = steps.length > 0 && currentStepIndex < 0
  useEffect(() => {
    if (redirect) {
      navigate(steps[0].path, { replace: true })
    }
  })

  const hasPrev = currentStepIndex > 0
  const hasNext = currentStepIndex < steps.length - 1

  const onPrev = () => hasPrev && navigate(steps[currentStepIndex - 1].path)
  const onNext = data => {
    if (hasNext) {
      onStep?.(data, currentStep)
      navigate(steps[currentStepIndex + 1].path)
    } else {
      onFinish?.(data, currentStep)
    }
  }

  const getWizardOutletProps = () => ({
    data,
    steps,
    hasPrev,
    hasNext,
    onPrev,
    onNext
  })

  const getWizardBreadcrumbProps = () => ({
    steps,
    currentStep
  })

  return {
    getWizardOutletProps,
    getWizardBreadcrumbProps
  }
}

export const useWizardBreadcrumb = props => {
  const { getWizardBreadcrumbProps } = useWizardContext()
  const { steps, currentStep } = getWizardBreadcrumbProps()

  return {
    steps,
    currentStep
  }
}

export const useWizardOutlet = props => {
  const { getWizardOutletProps } = useWizardContext()
  const { data, steps, onNext, onPrev } = getWizardOutletProps()

  const routes = useMemo(
    () =>
      steps.map(step => ({
        ...step,
        element: cloneElement(step.element, {
          data,
          onPrev,
          onNext
        })
      })),
    [steps, data, onPrev, onNext]
  )

  const element = useRoutes(routes)

  return {
    element
  }
}

export { WizardProvider, useWizardContext }
